<template>
  <Card class="stat-card">
    <div ref="svgContainer"></div>
    <!-- SVG Tooltip -->
    <div
      v-if="tooltip.visible"
      class="svg-tooltip"
      :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
    >
      <span class="tooltip-label">{{ tooltip.label }}</span>
      <span class="tooltip-value">{{ tooltip.value }} <span class="tooltip-unit">kW</span></span>
      <span class="tooltip-status" :class="tooltip.statusClass">● {{ tooltip.status }}</span>
    </div>
  </Card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import anime from "animejs/lib/anime.es.js";

// Maps power magnitude to animation duration — higher power = faster mover
function powerToDuration(kw) {
  const clamped = Math.min(Math.max(Math.abs(kw), 0), 1000);
  // 8000ms at 0 kW → 2500ms at 1000+ kW
  return 8000 - (clamped / 1000) * 5500;
}

export default {
  props: {
    mqttData: {
      type: Object,
      default: () => ({ gridPower: 0, dgPower: 0, solarPower: 0, totalPower: 0 })
    },
    sources: {
      type: Object,
      default: () => ({ grid: true, dg: true, spv: true })
    },
    mode: {
      type: String,
      default: "ZES"
    },
    breakerConfig: {
      type: Object,
      default: () => null
    }
  },

  data() {
    return {
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        label: '',
        value: 0,
        status: '',
        statusClass: '',
      }
    };
  },

  mounted() {
    this.createSVG();
    this.$nextTick(() => { this.animateMovers(); });

    this._resizeObserver = new ResizeObserver(() => {
      this.$nextTick(() => {
        if (!this.$refs.svgContainer) return;
        this.$refs.svgContainer.innerHTML = '';
        this.createSVG();
        this.$nextTick(() => { this.animateMovers(); });
      });
    });
    this._resizeObserver.observe(this.$refs.svgContainer);
  },

  computed: {
    ...mapGetters(["activePlantId", "getToken", "userId", "userEmail", "getGeneralState"]),
    isDark() { return this.getGeneralState('theme_dark'); }
  },

  methods: {
    ...mapActions(['logout']),

    getThemeColor(colorName) {
      const theme = this.$vuetify.theme;
      if (theme[colorName]) return theme[colorName];
      return getComputedStyle(document.documentElement).getPropertyValue(`--v-${colorName}-base`).trim();
    },

    createColorFilter(svgNS, svg, filterId, color) {
      const defs = svg.querySelector('defs') || (() => {
        const d = document.createElementNS(svgNS, 'defs');
        svg.insertBefore(d, svg.firstChild);
        return d;
      })();
      const existingInSVG = defs.querySelector('#' + filterId);
      if (existingInSVG) existingInSVG.remove();
      const filter = document.createElementNS(svgNS, 'filter');
      filter.setAttribute('id', filterId);
      filter.setAttribute('color-interpolation-filters', 'sRGB');
      const colorMatrix = document.createElementNS(svgNS, 'feColorMatrix');
      colorMatrix.setAttribute('type', 'saturate');
      colorMatrix.setAttribute('values', '0');
      colorMatrix.setAttribute('result', 'gray');
      const flood = document.createElementNS(svgNS, 'feFlood');
      flood.setAttribute('flood-color', color);
      flood.setAttribute('flood-opacity', '1');
      flood.setAttribute('result', 'flood');
      const composite = document.createElementNS(svgNS, 'feComposite');
      composite.setAttribute('in', 'flood');
      composite.setAttribute('in2', 'gray');
      composite.setAttribute('operator', 'in');
      filter.appendChild(colorMatrix);
      filter.appendChild(flood);
      filter.appendChild(composite);
      defs.appendChild(filter);
    },

    drawDottedLine(line, lineID, x1, y1, x2, y2) {
      line.setAttribute("id", lineID);
      line.setAttribute("x1", x1); line.setAttribute("y1", y1);
      line.setAttribute("x2", x2); line.setAttribute("y2", y2);
      line.setAttribute("stroke", this.getThemeColor('number'));
      line.setAttribute("stroke-width", "2");
      line.setAttribute("stroke-linecap", "round");
      line.setAttribute("stroke-dasharray", "6, 5");
    },

    drawPath(el, id, d, color) {
      el.setAttribute("id", id);
      el.setAttribute("d", d);
      el.setAttribute("stroke", color);
      el.setAttribute("stroke-width", "2");
      el.setAttribute("stroke-linecap", "round");
      el.setAttribute("stroke-linejoin", "round");
      el.setAttribute("fill", "none");
    },

    drawCircle(movingCircle, circleId, cx, cy, r, color) {
      movingCircle.setAttribute("id", circleId);
      movingCircle.setAttribute("cx", cx);
      movingCircle.setAttribute("cy", cy);
      movingCircle.setAttribute("r", r);
      movingCircle.setAttribute("fill", color);
    },

    drawText(text, x, y, color, fontSize, fontWeight, displayText, id) {
      text.setAttribute("x", x); text.setAttribute("y", y);
      text.setAttribute("fill", color);
      text.setAttribute("font-size", fontSize);
      text.setAttribute("font-weight", fontWeight);
      text.textContent = displayText;
      if (id) text.setAttribute("id", id);
    },

    drawImage(img, imageSVG, x, y, w, h, filterId = null) {
      img.setAttribute('href', imageSVG);
      img.setAttribute('x', x); img.setAttribute('y', y);
      img.setAttribute('width', w); img.setAttribute('height', h);
      if (filterId) img.setAttribute('filter', `url(#${filterId})`);
    },

    drawRect(rect, x, y, w, h, stroke, strokeWidth = "2", fill = "none", rx = "5", glow = false) {
      rect.setAttribute("x", x); rect.setAttribute("y", y);
      rect.setAttribute("width", w); rect.setAttribute("height", h);
      rect.setAttribute("rx", rx);
      rect.setAttribute("fill", fill);
      rect.setAttribute("stroke", stroke || "none");
      rect.setAttribute("stroke-width", strokeWidth);
      if (glow) rect.setAttribute("filter", "url(#rectShadow)");
    },

    vw(val) { return (parseFloat(val) / 100) * window.innerWidth; },
    vh(val) { return (parseFloat(val) / 100) * window.innerHeight; },

    // ─── Breaker helpers ─────────────────────────────────────────────────────

    _getBreakerStatus(source) {
      if (!this.breakerConfig) return 'unknown';
      const keyMap = { grid: 'og', dg: 'dg', spv: 'solar' };
      const cfgKey = keyMap[source];
      if (!cfgKey || !this.breakerConfig[cfgKey]) return 'unknown';
      const breakerId = this.breakerConfig[cfgKey].id;
      return (this.mqttData['breakers'] && this.mqttData['breakers']['breaker-' + breakerId]) || 'unknown';
    },

    _isBreakerOn(source) {
      return this._getBreakerStatus(source) !== 'off';
    },

    _updateBreakerVisuals() {
      ['grid', 'dg', 'spv'].forEach(source => {
        if (!this.sources[source]) return;
        const status = this._getBreakerStatus(source);
        const isOff = status === 'off';
        const isUnknown = status === 'unknown';
        const color = isOff ? '#f44336' : isUnknown ? '#888888' : '#4CAF50';

        // ── Breaker dots + line color ──
        ['a', 'b'].forEach(side => {
          const dot = this._getSVGEl(`breaker-${side}-${source}`);
          if (dot) dot.setAttribute('fill', color);
        });
        const line = this._getSVGEl(`breaker-line-${source}`);
        if (line) {
          line.setAttribute('stroke', color);
          line.removeAttribute('stroke-dasharray');
        }

        // ── Status label (ON / OFF / ...) ──
        const label = this._getSVGEl(`breaker-label-${source}`);
        if (label) {
          label.textContent = isOff ? 'OFF' : isUnknown ? 'N/A' : 'ON';
          label.setAttribute('fill', color);
        }

        // ── Path styling ──
        const path = this._getSVGEl(`path-${source}`);
        if (path) {
          path.setAttribute('stroke', this.getThemeColor('primaryText'));
          path.removeAttribute('stroke-dasharray');
          path.setAttribute('opacity', '1');
        }
      });
    },

    _getSVGEl(id) {
      if (!this.$refs.svgContainer) return null;
      return this.$refs.svgContainer.querySelector(`#${id}`);
    },

    // ─── Layout helpers ───────────────────────────────────────────────────────

    _getSourceLayout() {
      const slots = [
        { srcY: this.vh(10), textValue: this.vh(9.5), loadY: this.vh(28), labelY: this.vh(17), imageY: this.vh(3), dottedX: this.vw(2), moverY: this.vh(9.4) },
        { srcY: this.vh(30), textValue: this.vh(29.5), loadY: this.vh(30), labelY: this.vh(36), imageY: this.vh(23), dottedX: this.vw(4), moverY: this.vh(29.3) },
        { srcY: this.vh(49), textValue: this.vh(48.5), loadY: this.vh(32), labelY: this.vh(57), imageY: this.vh(43), dottedX: this.vw(6.25), moverY: this.vh(48.3) },
      ];
      const order = ['grid', 'dg', 'spv'];
      const active = order.filter(s => this.sources[s]);
      const layout = {};
      active.forEach((src, i) => { layout[src] = slots[i]; });
      return layout;
    },

    _makePipePath(srcY, loadY) {
      const srcX = this.vw(21);
      const midX = this.vw(25);
      const endX = this.vw(42);
      const cornerX = this.vw(24.5);
      const cornerX2 = this.vw(25.5);
      if (Math.abs(srcY - loadY) < 2) return `M ${srcX} ${srcY} L ${endX} ${srcY}`;
      const dir = loadY > srcY ? 1 : -1;
      return `M ${srcX} ${srcY} L ${cornerX} ${srcY} Q ${midX} ${srcY} ${midX} ${srcY + dir * this.vh(0.5)} L ${midX} ${loadY - dir * this.vh(0.5)} Q ${midX} ${loadY} ${cornerX2} ${loadY} L ${endX} ${loadY}`;
    },

    // ─── Tooltip ──────────────────────────────────────────────────────────────

    _attachSourceTooltip(el, label, powerKey) {
      el.style.cursor = 'pointer';
      el.addEventListener('mouseenter', (e) => {
        const power = this.mqttData[powerKey] || 0;
        const isActive = power > 0;
        const containerRect = this.$refs.svgContainer.getBoundingClientRect();
        this.tooltip = {
          visible: true,
          x: e.clientX - containerRect.left + 10,
          y: e.clientY - containerRect.top + 10,
          label,
          value: power,
          status: isActive ? 'Active' : 'Inactive',
          statusClass: isActive ? 'status-active' : 'status-inactive',
        };
      });
      el.addEventListener('mousemove', (e) => {
        const containerRect = this.$refs.svgContainer.getBoundingClientRect();
        this.tooltip.x = e.clientX - containerRect.left + 10;
        this.tooltip.y = e.clientY - containerRect.top + 10;
        // Update live power value on move
        this.tooltip.value = this.mqttData[powerKey] || 0;
      });
      el.addEventListener('mouseleave', () => { this.tooltip.visible = false; });
    },

    // ─── SVG creation ─────────────────────────────────────────────────────────

    createSVG() {
      const svgNS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(svgNS, "svg");
      const W = this.vw(60);
      const H = this.vh(78);
      svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
      svg.setAttribute("width", "60vw");
      svg.setAttribute("height", "72vh");

      const defs = document.createElementNS(svgNS, "defs");

      // ── Drop shadow for LMS rect ──
      const filter = document.createElementNS(svgNS, "filter");
      filter.setAttribute("id", "rectShadow");
      filter.setAttribute("x", "-50%"); filter.setAttribute("y", "-50%");
      filter.setAttribute("width", "200%"); filter.setAttribute("height", "200%");
      const dropShadow = document.createElementNS(svgNS, "feDropShadow");
      dropShadow.setAttribute("dx", "2"); dropShadow.setAttribute("dy", "2");
      dropShadow.setAttribute("stdDeviation", "3");
      dropShadow.setAttribute("flood-color", "#ED982B");
      dropShadow.setAttribute("flood-opacity", "0.7");
      const animateSD = document.createElementNS(svgNS, 'animate');
      animateSD.setAttribute('attributeName', 'stdDeviation');
      animateSD.setAttribute('values', '2;6;2');
      animateSD.setAttribute('dur', '2s');
      animateSD.setAttribute('repeatCount', 'indefinite');
      dropShadow.appendChild(animateSD);
      filter.appendChild(dropShadow);

      // ── Glow filter for LMS indicator circle ──
      const circleGlowFilter = document.createElementNS(svgNS, "filter");
      circleGlowFilter.setAttribute("id", "circleGlow");
      circleGlowFilter.setAttribute("x", "-100%"); circleGlowFilter.setAttribute("y", "-100%");
      circleGlowFilter.setAttribute("width", "300%"); circleGlowFilter.setAttribute("height", "300%");
      const circleBlur = document.createElementNS(svgNS, "feGaussianBlur");
      circleBlur.setAttribute("in", "SourceGraphic");
      circleBlur.setAttribute("stdDeviation", "3");
      circleBlur.setAttribute("result", "blur");
      const animateCircleSD = document.createElementNS(svgNS, 'animate');
      animateCircleSD.setAttribute('attributeName', 'stdDeviation');
      animateCircleSD.setAttribute('values', '2;6;2');
      animateCircleSD.setAttribute('dur', '1.5s');
      animateCircleSD.setAttribute('repeatCount', 'indefinite');
      circleBlur.appendChild(animateCircleSD);
      const circleMerge = document.createElementNS(svgNS, "feMerge");
      ['blur', 'SourceGraphic'].forEach(n => {
        const node = document.createElementNS(svgNS, "feMergeNode");
        node.setAttribute("in", n);
        circleMerge.appendChild(node);
      });
      circleGlowFilter.appendChild(circleBlur);
      circleGlowFilter.appendChild(circleMerge);
      defs.appendChild(circleGlowFilter);
      defs.appendChild(filter);
      svg.appendChild(defs);

      const finalArray = [];
      const layout = this._getSourceLayout();

      const svgDetails = [
        // ── LIVE indicator ──
        { type: "circle", objId: "live-indicator", xAxis: this.vw(0), yAxis: this.vh(2.2), radius: this.vh(0.5), color: "#FF5252" },
        { type: "text", xAxis: this.vw(0.5), yAxis: this.vh(2.8), color: this.getThemeColor('error'), fontSize: this.vh(1.5), text: "LIVE" },

        // ── Legend ──
        { type: "dottedLine", objId: "legend-comm-line", xAxis: this.vw(24), yAxis: this.vh(2.5), xAxis2: this.vw(27.5), yAxis2: this.vh(2.5) },
        { type: "text", xAxis: this.vw(28), yAxis: this.vh(3), color: this.getThemeColor('primaryText'), fontSize: this.vh(1.6), text: "Communication" },
        { type: "rect", objId: "legend-flow-bar", xAxis: this.vw(35), yAxis: this.vh(1.8), width: this.vw(1.5), height: this.vh(1.2), stroke: "none", strokeWidth: "0", fill: "#668CF6", rx: "3" },
        { type: "text", xAxis: this.vw(37), yAxis: this.vh(3), color: this.getThemeColor('primaryText'), fontSize: this.vh(1.6), text: "Power Flow (kW)" },
        { type: "rect", objId: "legend-curtail-bar", xAxis: this.vw(44.5), yAxis: this.vh(1.8), width: this.vw(1.5), height: this.vh(1.2), stroke: "none", strokeWidth: "0", fill: "#ED982B", rx: "3" },
        { type: "text", xAxis: this.vw(46.3), yAxis: this.vh(3), color: this.getThemeColor('primaryText'), fontSize: this.vh(1.6), text: "Command (kW)" },

        // ── GRID ──
        ...(this.sources.grid ? (() => {
          const s = layout.grid;
          const power = this.mqttData.gridPower != null ? this.mqttData.gridPower : 'N/A';
          const pathColor = power > 0 ? this.getThemeColor('primaryText') : '#555555';
          const pathOpacity = power > 0 ? '1' : '0.35';
          return [
            { type: 'image', image: require('@/assets/grid.svg'), xAxis: this.vw(15), yAxis: s.imageY, width: this.vw(5), height: this.vh(14), color: this.getThemeColor('extraGrey'), filterId: 'gridFilter', tooltipLabel: 'Grid', tooltipKey: 'gridPower' },
            { type: "path", objId: "path-grid", d: this._makePipePath(s.srcY, s.loadY), color: pathColor, opacity: pathOpacity },
            { type: "dottedLine", objId: "dotted-grid", xAxis: this.vw(14.5), yAxis: s.srcY, xAxis2: this.vw(2), yAxis2: s.srcY },
            { type: "dottedLine", objId: "dotted-grid-2", xAxis: this.vw(2), yAxis: s.srcY, xAxis2: this.vw(2), yAxis2: this.vh(65) },
            { type: "rect", objId: "mover-grid", xAxis: this.vw(21), yAxis: s.moverY, width: this.vw(1.5), height: this.vh(1.2), stroke: "none", strokeWidth: "0", fill: "#668CF6", rx: "4" },
            { type: "text", xAxis: this.vw(11.5), yAxis: s.textValue, color: this.getThemeColor('primaryText'), fontSize: this.vh(2), text: power, id: "gridPowerText" },
            { type: "text", xAxis: this.vw(16.2), yAxis: s.labelY, fontSize: this.vh(2), fontWeight: "900", color: this.getThemeColor('primaryText'), text: "GRID" },
          ];
        })() : []),

        // ── DG ──
        ...(this.sources.dg ? (() => {
          const s = layout.dg;
          const power = this.mqttData.dgPower != null ? this.mqttData.dgPower : 'N/A';
          const pathColor = power > 0 ? this.getThemeColor('primaryText') : '#555555';
          const pathOpacity = power > 0 ? '1' : '0.35';
          return [
            { type: 'image', image: require('@/assets/DG.svg'), xAxis: this.vw(15), yAxis: s.imageY, width: this.vw(5), height: this.vh(12), color: this.getThemeColor('extraGrey'), filterId: 'dgFilter', tooltipLabel: 'Diesel Generator', tooltipKey: 'dgPower' },
            { type: "path", objId: "path-dg", d: this._makePipePath(s.srcY, s.loadY), color: pathColor, opacity: pathOpacity },
            { type: "dottedLine", objId: "dotted-dg", xAxis: this.vw(14.5), yAxis: s.srcY, xAxis2: s.dottedX, yAxis2: s.srcY },
            { type: "dottedLine", objId: "dotted-dg-2", xAxis: s.dottedX, yAxis: s.srcY, xAxis2: s.dottedX, yAxis2: this.vh(65) },
            { type: "rect", objId: "mover-dg", xAxis: this.vw(21), yAxis: s.moverY, width: this.vw(1.5), height: this.vh(1.2), stroke: "none", strokeWidth: "0", fill: "#668CF6", rx: "4" },
            { type: "text", xAxis: this.vw(11.5), yAxis: s.textValue, color: this.getThemeColor('primaryText'), fontSize: this.vh(2), text: power, id: "dgPowerText" },
            { type: "text", xAxis: this.vw(16.7), yAxis: s.labelY, fontSize: this.vh(2), fontWeight: "900", color: this.getThemeColor('primaryText'), text: "DG" },
          ];
        })() : []),

        // ── SPV ──
        (() => {
          const power = this.mqttData.solarPower != null ? this.mqttData.solarPower : 'N/A';
          const pathColor = power > 0 ? this.getThemeColor('primaryText') : '#555555';
          const pathOpacity = power > 0 ? '1' : '0.35';
          return [
            { type: 'image', image: require('@/assets/SPV.svg'), xAxis: this.vw(15), yAxis: this.vh(43), width: this.vw(5.5), height: this.vh(12), color: this.getThemeColor('extraGrey'), filterId: 'spvFilter', tooltipLabel: 'Solar PV', tooltipKey: 'solarPower' },
            { type: "path", objId: "path-spv", d: `M ${this.vw(21)} ${this.vh(49)} L ${this.vw(24.5)} ${this.vh(49)} Q ${this.vw(25)} ${this.vh(49)} ${this.vw(25)} ${this.vh(48.5)} L ${this.vw(25)} ${this.vh(32.5)} Q ${this.vw(25)} ${this.vh(32)} ${this.vw(25.5)} ${this.vh(32)} L ${this.vw(42)} ${this.vh(32)}`, color: pathColor, opacity: pathOpacity },
            { type: "dottedLine", objId: "dotted-spv", xAxis: this.vw(14.5), yAxis: this.vh(49), xAxis2: this.vw(6.25), yAxis2: this.vh(49) },
            { type: "dottedLine", objId: "dotted-spv-2", xAxis: this.vw(6.25), yAxis: this.vh(49), xAxis2: this.vw(6.25), yAxis2: this.vh(65) },
            { type: "rect", objId: "mover-spv", xAxis: this.vw(21), yAxis: this.vh(48.3), width: this.vw(1.5), height: this.vh(1.2), stroke: "none", strokeWidth: "0", fill: "#668CF6", rx: "4" },
            { type: "text", xAxis: this.vw(11.5), yAxis: this.vh(48.5), color: this.getThemeColor('primaryText'), fontSize: this.vh(2), text: power, id: "solarPowerText" },
            { type: "text", xAxis: this.vw(17.2), yAxis: this.vh(57), fontSize: this.vh(2), fontWeight: "900", color: this.getThemeColor('primaryText'), text: "SPV" },
          ];
        })(),

        // ── LOAD ──
        { type: 'image', image: require('@/assets/factory.svg'), xAxis: this.vw(43), yAxis: this.vh(22), width: this.vw(5), height: this.vh(12), color: this.getThemeColor('extraGrey'), filterId: 'factoryFilter', tooltipLabel: 'Load', tooltipKey: 'totalPower' },
        { type: "dottedLine", objId: "dotted-factory", xAxis: this.vw(45.5), yAxis: this.vh(37), xAxis2: this.vw(45.5), yAxis2: this.vh(69) },
        { type: "dottedLine", objId: "dotted-factory-2", xAxis: this.vw(45.5), yAxis: this.vh(69), xAxis2: this.vw(8), yAxis2: this.vh(69) },
        { type: "text", xAxis: this.vw(48.4), yAxis: this.vh(31), color: this.getThemeColor('primaryText'), fontSize: this.vh(2), text: this.mqttData["totalPower"] != null ? this.mqttData["totalPower"] : "N/A", id: "totalPowerText" },
        { type: "text", xAxis: this.vw(44), yAxis: this.vh(35), fontSize: this.vh(2), fontWeight: "900", color: this.getThemeColor('primaryText'), text: "LOAD" },

        // ── LMS/ZES box ──
        { type: "rect", objId: "lms", xAxis: this.vw(0), yAxis: this.vh(65), width: this.vw(8), height: this.vh(8), stroke: this.mqttData.isLive ? '#ED982B' : '#7a5a1a', strokeWidth: "2", glow: !!this.mqttData.isLive },
        { type: "circle", objId: "lms-indicator", xAxis: this.vw(7), yAxis: this.vh(66.5), radius: this.vh(0.5), color: this.mqttData.acPowerSet != null ? "#ED982B" : "#7a5a1a" },

        // ── Breakers ──
        ...(this.breakerConfig ? (() => {
          const items = [];
          [{ source: 'grid', cfgKey: 'og' }, { source: 'dg', cfgKey: 'dg' }, { source: 'spv', cfgKey: 'solar' }]
            .forEach(({ source, cfgKey }) => {
              if (!this.sources[source] || !this.breakerConfig[cfgKey]) return;
              const by = source === 'spv' ? this.vh(49) : (layout[source] ? layout[source].srcY : null);
              if (by === null) return;
              const bx = this.vw(23.5);
              const gap = this.vw(0.55);
              items.push({ type: 'circle', objId: `breaker-a-${source}`, xAxis: bx - gap, yAxis: by, radius: this.vh(0.42), color: '#888888' });
              items.push({ type: 'circle', objId: `breaker-b-${source}`, xAxis: bx + gap, yAxis: by, radius: this.vh(0.42), color: '#888888' });
              items.push({ type: 'breakerLine', objId: `breaker-line-${source}`, x1: bx - gap, y1: by, x2: bx + gap, y2: by, color: '#888888' });
              // ✅ Status label below the breaker
              items.push({ type: 'text', id: `breaker-label-${source}`, xAxis: bx - this.vw(0.6), yAxis: by + this.vh(2.2), color: '#888888', fontSize: this.vh(1.4), fontWeight: '700', text: 'N/A' });
            });
          return items;
        })() : []),

        // ── LMS curtail path + mover ──
        { type: "path", objId: "path-lms-spv", d: `M ${this.vw(6.25)} ${this.vh(64)} L ${this.vw(6.25)} ${this.vh(49)} L ${this.vw(15)} ${this.vh(49)}`, color: "transparent" },
        { type: "rect", objId: "mover-lms-spv", xAxis: this.vw(7), yAxis: this.vh(69), width: this.vw(1.5), height: this.vh(1.2), stroke: "none", strokeWidth: "0", fill: "#ED982B", rx: "4" },
        { type: "text", id: "lmsCurtailValue", xAxis: this.vw(2.5), yAxis: this.vh(71), color: "#ED982B", fontSize: this.vh(2), text: "" },
        { type: "text", id: "lmsText", xAxis: this.vw(2.5), yAxis: this.vh(70), fontSize: this.vh(2), fontWeight: "900", color: this.mqttData.isLive ? "#ED982B" : "#7a5a1a", text: this.mode === "zes" ? "ZES" : "LMS" },
      ].flat();

      const svgTagMap = {
        line: 'path', dottedLine: 'line', breakerLine: 'line',
        image: 'image', path: 'path', circle: 'circle', text: 'text', rect: 'rect',
      };

      const drawDispatch = {
        image: (el, obj) => {
          if (obj.color && obj.filterId) this.createColorFilter(svgNS, svg, obj.filterId, obj.color);
          this.drawImage(el, obj.image, obj.xAxis, obj.yAxis, obj.width, obj.height, obj.filterId || null);
          if (obj.tooltipLabel) this._attachSourceTooltip(el, obj.tooltipLabel, obj.tooltipKey);
        },
        path: (el, obj) => {
          this.drawPath(el, obj.objId, obj.d, obj.color || this.getThemeColor('primaryText'));
          if (obj.opacity) el.setAttribute('opacity', obj.opacity);
        },
        dottedLine: (el, obj) => this.drawDottedLine(el, obj.objId, obj.xAxis, obj.yAxis, obj.xAxis2, obj.yAxis2),
        breakerLine: (el, obj) => {
          el.setAttribute('id', obj.objId);
          el.setAttribute('x1', obj.x1); el.setAttribute('y1', obj.y1);
          el.setAttribute('x2', obj.x2); el.setAttribute('y2', obj.y2);
          el.setAttribute('stroke', obj.color || '#888888');
          el.setAttribute('stroke-width', '2.5');
          el.setAttribute('stroke-linecap', 'round');
        },
        circle: (el, obj) => this.drawCircle(el, obj.objId, obj.xAxis, obj.yAxis, obj.radius, obj.color),
        text: (el, obj) => this.drawText(el, obj.xAxis, obj.yAxis, obj.color, obj.fontSize, obj.fontWeight, obj.text, obj.id),
        rect: (el, obj) => {
          this.drawRect(el, obj.xAxis, obj.yAxis, obj.width, obj.height,
            obj.stroke || this.getThemeColor('primaryText'),
            obj.strokeWidth || '2', obj.fill || 'none', obj.rx || '5', obj.glow || false);
          if (obj.objId) el.setAttribute('id', obj.objId);
        },
      };

      svgDetails.forEach(obj => {
        const tag = svgTagMap[obj.type] || obj.type;
        const el = document.createElementNS(svgNS, tag);
        const drawer = drawDispatch[obj.type];
        if (drawer) drawer(el, obj);
        finalArray.push(el);
      });

      const lmsMoverEl = finalArray.find(el => el.getAttribute && el.getAttribute('id') === 'mover-lms-spv');
      const lmsCurtailEl = finalArray.find(el => el.getAttribute && el.getAttribute('id') === 'lmsCurtailValue');
      if (lmsMoverEl) lmsMoverEl.setAttribute('display', 'none');
      if (lmsCurtailEl) lmsCurtailEl.setAttribute('display', 'none');

      finalArray.forEach(el => svg.appendChild(el));
      this.$refs.svgContainer.appendChild(svg);
    },

    // ─── SVG update helpers ────────────────────────────────────────────────────

    _updateSVGText(id, value) {
      const el = this._getSVGEl(id);
      if (el) el.textContent = value;
    },

    _stopAnimations() {
      ['mover-grid', 'mover-dg', 'mover-spv', 'mover-lms-spv'].forEach(id => {
        const el = this._getSVGEl(id);
        if (el) anime.remove(el);
      });
    },

    _animateAlongPath(el, path, duration) {
      if (!el || !path) return;
      const pathLength = path.getTotalLength();
      anime({
        targets: el,
        duration: duration || 6000,
        easing: 'linear',
        loop: true,
        update(anim) {
          const dist = (anim.progress / 100) * pathLength;
          const point = path.getPointAtLength(dist);
          const ahead = path.getPointAtLength(Math.min(dist + 1, pathLength));
          const angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * (180 / Math.PI);
          const w = el._animW || 0;
          const h = el._animH || 0;
          el.setAttribute('x', -w / 2);
          el.setAttribute('y', -h / 2);
          el.setAttribute('transform', `translate(${point.x}, ${point.y}) rotate(${angle})`);
        }
      });
    },

    // Power-proportional animation speed
    animateMovers() {
      const movers = [
        { id: 'mover-grid', pathId: 'path-grid', powerKey: 'gridPower', source: 'grid' },
        { id: 'mover-dg', pathId: 'path-dg', powerKey: 'dgPower', source: 'dg' },
        { id: 'mover-spv', pathId: 'path-spv', powerKey: 'solarPower', source: 'spv' },
      ];
      movers.forEach(({ id, pathId, powerKey, source }) => {
        if (!this._isBreakerOn(source)) return;
        const power = this.mqttData[powerKey] || 0;
        if (power <= 0) return;
        const el = this._getSVGEl(id);
        const path = this._getSVGEl(pathId);
        if (el) { el._animW = this.vw(1.2); el._animH = this.vh(1.2); }
        // Speed scales with power magnitude
        this._animateAlongPath(el, path, powerToDuration(power));
      });
    },

    animateLMSCurtail(acPowerSet) {
      const el = this._getSVGEl('mover-lms-spv');
      const path = this._getSVGEl('path-lms-spv');
      const label = this._getSVGEl('lmsCurtailValue');
      if (!el || !path) return;
      el.setAttribute('display', 'block');
      if (label) {
        const total = Array.isArray(acPowerSet)
          ? acPowerSet.reduce((s, v) => s + Number(v), 0)
          : Number(acPowerSet);
        label.textContent = total;
        label.setAttribute('display', 'block');
      }
      if (el) { el._animW = this.vw(1.2); el._animH = this.vh(1.2); }
      this._animateAlongPath(el, path, powerToDuration(acPowerSet));
    },
  },

  watch: {
    isDark() {
      this.$nextTick(() => {
        this.$refs.svgContainer.innerHTML = '';
        this.createSVG();
      });
    },
    sources: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (!this.$refs.svgContainer) return;
          this.$refs.svgContainer.innerHTML = '';
          this.createSVG();
          this.$nextTick(() => { this.animateMovers(); });
        });
      }
    },
    mqttData: {
      deep: true,
      handler(val) {
        this._updateSVGText('gridPowerText', val.gridPower != null ? val.gridPower : 'N/A');
        this._updateSVGText('dgPowerText', val.dgPower != null ? val.dgPower : 'N/A');
        this._updateSVGText('solarPowerText', val.solarPower != null ? val.solarPower : 'N/A');
        this._updateSVGText('totalPowerText', val.totalPower != null ? val.totalPower : 'N/A');

        // Update path opacity based on whether power > 0
        ['grid', 'dg'].forEach(src => {
          const powerKey = src === 'grid' ? 'gridPower' : 'dgPower';
          const path = this._getSVGEl(`path-${src}`);
          if (path) path.setAttribute('opacity', (val[powerKey] || 0) > 0 ? '1' : '0.35');
        });
        const spvPath = this._getSVGEl('path-spv');
        if (spvPath) spvPath.setAttribute('opacity', (val.solarPower || 0) > 0 ? '1' : '0.35');

        const isLive = !!val.isLive;
        const isActive = val.acPowerSet != null;
        const lmsRect = this._getSVGEl('lms');
        const lmsText = this._getSVGEl('lmsText');
        const curtailLabel = this._getSVGEl('lmsCurtailValue');
        const lmsMover = this._getSVGEl('mover-lms-spv');
        const lmsIndicator = this._getSVGEl('lms-indicator');

        if (lmsIndicator) {
          lmsIndicator.setAttribute('fill', isLive ? '#ED982B' : '#7a5a1a');
          if (isLive) lmsIndicator.setAttribute('filter', 'url(#circleGlow)');
          else lmsIndicator.removeAttribute('filter');
        }
        if (lmsRect) {
          lmsRect.setAttribute('stroke', isLive ? '#ED982B' : '#7a5a1a');
          lmsRect.setAttribute('stroke-width', isLive ? '2' : '1.5');
          if (isLive) lmsRect.setAttribute('filter', 'url(#rectShadow)');
          else lmsRect.removeAttribute('filter');
        }
        if (lmsText) {
          lmsText.setAttribute('fill', isLive ? '#ED982B' : '#7a5a1a');
          lmsText.setAttribute('y', isActive ? this.vh(68.8) : this.vh(70));
        }
        if (curtailLabel && !isActive) curtailLabel.textContent = '';
        if (lmsMover) lmsMover.setAttribute('display', isActive ? 'block' : 'none');

        this._updateBreakerVisuals();
        this._stopAnimations();
        this.$nextTick(() => {
          this.animateMovers();
          if (isActive) this.animateLMSCurtail(val.acPowerSet);
        });
      }
    },
    breakerConfig: {
      deep: true,
      handler(val) {
        this.$nextTick(() => {
          if (!this.$refs.svgContainer) return;
          this.$refs.svgContainer.innerHTML = '';
          this.createSVG();
          this.$nextTick(() => { this.animateMovers(); });
        });
      }
    }
  },

  beforeDestroy() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },
};
</script>

<style scoped>
.stat-card {
  flex: 1;
  gap: 6px;
  box-shadow: 0px 5px 5px 0px #334079;
  border-radius: 8px;
  position: relative; /* needed for tooltip absolute positioning */
  overflow: visible;
}

/* ── SVG Tooltip ─────────────────────────────────── */
.svg-tooltip {
  position: absolute;
  pointer-events: none;
  background: rgba(10, 18, 50, 0.92);
  border: 1px solid #334079;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  z-index: 100;
  backdrop-filter: blur(4px);
  min-width: 120px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
}

.tooltip-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8899cc;
}

.tooltip-value {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

.tooltip-unit {
  font-size: 12px;
  font-weight: 400;
  color: #8899cc;
}

.tooltip-status {
  font-size: 11px;
  font-weight: 600;
}

.status-active { color: #4CAF50; }
.status-inactive { color: #EF5350; }
</style>
